import React, { useRef, useState } from "react"
import { motion } from "framer-motion"
import { useFormik } from "formik"
import * as Yup from "yup"
import { FiUserPlus, FiArrowLeft } from "react-icons/fi"
import { HiOutlineShieldCheck } from "react-icons/hi2"
import { useHistory } from "react-router-dom"
import { useDispatch } from "react-redux"
import FieldInput from "../../Components/FieldInput"
import Button from "../../Components/Button"
import API from "../../Common/API"
import { setAlert } from "../../Store/Theme/actions"
import { setAccountType, setLoggedIn, setToken, setUserDetails } from "../../Store/Auth/actions"
import logo from "../../Assets/Images/rklogo.png"

const GENDER_OPTIONS = [
    { label: "Male", value: "Male" },
    { label: "Female", value: "Female" },
]

const registerSchema = Yup.object().shape({
    firstName: Yup.string().min(2, "At least 2 characters").max(80).required("First name is required"),
    lastName: Yup.string().min(1, "At least 1 character").max(80).required("Last name is required"),
    mobileNumber: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
        .required("Mobile number is required"),
    gender: Yup.string().oneOf(["Male", "Female"], "Select a gender").required("Gender is required"),
    password: Yup.string().min(8, "Minimum 8 characters").max(128).required("Password is required"),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords do not match")
        .required("Please confirm your password"),
})

const MemberRegister = () => {
    const history = useHistory()
    const dispatch = useDispatch()
    const [submitting, setSubmitting] = useState(false)
    // OTP verification step
    const [pendingMobile, setPendingMobile] = useState(null)  // set after register → show OTP screen
    const [devOtp, setDevOtp] = useState(null)
    const [otp, setOtp] = useState(["", "", "", "", "", ""])
    const [otpError, setOtpError] = useState("")
    const [verifying, setVerifying] = useState(false)
    const [resendCooldown, setResendCooldown] = useState(0)
    const otpRefs = useRef([])
    const cooldownRef = useRef(null)

    const startCooldown = () => {
        setResendCooldown(60)
        cooldownRef.current = setInterval(() => {
            setResendCooldown((c) => { if (c <= 1) { clearInterval(cooldownRef.current); return 0 } return c - 1 })
        }, 1000)
    }

    const handleOtpChange = (index, value) => {
        if (!/^\d?$/.test(value)) return
        const next = [...otp]; next[index] = value; setOtp(next)
        if (value && index < 5) otpRefs.current[index + 1]?.focus()
    }
    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus()
    }

    const doAutoLogin = (token) => {
        const userDetails = JSON.parse(window.atob(token.split(".")[1]))
        localStorage.setItem("token", token)
        localStorage.setItem("accountType", "member")
        localStorage.setItem("userDetails", JSON.stringify(userDetails))
        dispatch(setUserDetails(userDetails))
        dispatch(setAccountType("member"))
        dispatch(setToken(token))
        dispatch(setLoggedIn(true))
    }

    const verifyOtp = async () => {
        const code = otp.join("")
        if (code.length < 6) { setOtpError("Enter the complete 6-digit OTP"); return }
        setVerifying(true); setOtpError("")
        try {
            const res = await API.post("/auth/verifyRegistrationOtp", { mobileNumber: pendingMobile, otp: code })
            if (res.status === 200 && res.data?.data) {
                doAutoLogin(res.data.data)
                dispatch(setAlert({ title: "Welcome!", subtitle: "Account verified and created successfully.", type: "success", active: true }))
                history.push("/member")
            }
        } catch (err) {
            setOtpError(err?.response?.data?.error || "Verification failed. Please try again.")
        } finally {
            setVerifying(false)
        }
    }

    const form = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            mobileNumber: "",
            gender: "",
            password: "",
            confirmPassword: "",
        },
        validationSchema: registerSchema,
        validateOnMount: false,
        validateOnBlur: true,
        onSubmit: async (values) => {
            setSubmitting(true)
            try {
                const res = await API.post("/auth/register", {
                    firstName: values.firstName.trim(),
                    lastName: values.lastName.trim(),
                    mobileNumber: values.mobileNumber.replace(/\D/g, ""),
                    gender: values.gender,
                    password: values.password,
                })

                if (res.status === 200 && res.data?.data?.pendingVerification) {
                    const { mobileNumber, otp: returnedOtp } = res.data.data
                    setPendingMobile(mobileNumber)
                    setDevOtp(returnedOtp || null)
                    startCooldown()
                }
            } catch (err) {
                const message =
                    err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    "Registration failed. Please try again."
                dispatch(setAlert({ title: "Error", subtitle: message, type: "error", active: true }))
            } finally {
                setSubmitting(false)
            }
        },
    })

    // ── OTP Verification screen ──────────────────────────────────────────────
    if (pendingMobile) {
        return (
            <motion.div
                style={{ minHeight: "100vh", background: "linear-gradient(160deg,#FFFDF5 0%,#F0EBD8 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 16px" }}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            >
                <div className="w-full max-w-md p-8" style={{ borderRadius: 32, border: "1.5px solid rgba(224,207,160,0.4)", background: "#fff", boxShadow: "0 12px 40px rgba(10,22,40,0.12)" }}>
                    <div className="flex justify-center mb-6">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                            <HiOutlineShieldCheck className="h-8 w-8 text-emerald-600" />
                        </div>
                    </div>
                    <p className="text-xs font-bold uppercase tracking-[0.22em] text-primeColor text-center">Step 2 of 2</p>
                    <h2 className="mt-2 text-2xl font-semibold text-tColor text-center">Verify Mobile Number</h2>
                    <p className="mt-3 text-sm leading-6 text-tpColor text-center">
                        An OTP has been sent to <strong>{pendingMobile}</strong>. Enter it below to complete registration.
                    </p>

                    {devOtp && (
                        <div className="mt-5 rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50 px-4 py-3 text-center">
                            <p className="text-xs font-bold uppercase tracking-wider text-amber-600">Testing Mode — Your OTP</p>
                            <p className="mt-1 text-3xl font-bold tracking-[0.3em] text-amber-700">{devOtp}</p>
                            <p className="mt-1 text-xs text-amber-500">(SMS will be sent in production)</p>
                        </div>
                    )}

                    <div className="mt-6 space-y-2">
                        <label className="text-sm font-semibold text-tColor">Enter 6-digit OTP</label>
                        <div className="flex gap-2 justify-center">
                            {otp.map((digit, i) => (
                                <input key={i} ref={(el) => (otpRefs.current[i] = el)}
                                    type="text" inputMode="numeric" maxLength={1} value={digit}
                                    onChange={(e) => handleOtpChange(i, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                    className="h-12 w-10 rounded-xl border-2 border-borderColor bg-slate-50 text-center text-lg font-bold outline-none focus:border-primeColor focus:bg-white transition"
                                />
                            ))}
                        </div>
                    </div>

                    {otpError && <p className="mt-3 rounded-xl bg-red-50 px-4 py-2 text-xs font-semibold text-red-600">{otpError}</p>}

                    <button
                        type="button"
                        onClick={verifyOtp}
                        disabled={verifying}
                        className="mt-5 w-full rounded-2xl bg-primeColor py-3 text-sm font-bold text-white transition hover:bg-primeDark disabled:opacity-60"
                    >
                        {verifying ? "Verifying…" : "Verify & Create Account"}
                    </button>

                    <div className="mt-4 text-center text-xs text-tpColor">
                        {resendCooldown > 0
                            ? <span>Resend OTP in <strong className="text-tColor">{resendCooldown}s</strong></span>
                            : <button type="button" onClick={() => form.submitForm()} className="font-bold text-primeColor hover:underline">Resend OTP</button>
                        }
                    </div>

                    <div className="mt-4 text-center">
                        <button type="button" onClick={() => { setPendingMobile(null); setOtp(["","","","","",""]); setOtpError("") }}
                            className="text-xs text-tpColor hover:text-primeColor">
                            ← Back to registration
                        </button>
                    </div>
                </div>
            </motion.div>
        )
    }

    return (
        <motion.div
            style={{ minHeight: "100vh", background: "linear-gradient(160deg,#FFFDF5 0%,#F0EBD8 100%)", padding: "24px 16px" }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        >
            <div style={{ maxWidth: 1180, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", minHeight: "calc(100vh - 80px)", borderRadius: 36, overflow: "hidden", boxShadow: "0 24px 64px rgba(10,22,40,0.18)", border: "1px solid rgba(200,146,10,0.15)" }}>

                {/* ── Left panel – navy ── */}
                <div style={{ background: "linear-gradient(160deg,#0A1628 0%,#060E1A 100%)", padding: "48px 44px", display: "flex", flexDirection: "column", justifyContent: "space-between", borderRight: "1px solid rgba(200,146,10,0.15)" }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 40 }}>
                            <div style={{ width: 64, height: 64, borderRadius: 16, overflow: "hidden", border: "1.5px solid rgba(200,146,10,0.4)", boxShadow: "0 4px 16px rgba(200,146,10,0.2)", background: "#fff", flexShrink: 0 }}>
                                <img src="/landing/images/logo.jpeg" alt="Royal Kavery" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.style.display = "none" }} />
                            </div>
                            <div>
                                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(245,210,100,0.6)", marginBottom: 3 }}>Royal Kavery</p>
                                <h1 style={{ fontSize: 22, fontWeight: 800, color: "#F5D264", fontFamily: "'Playfair Display',serif", lineHeight: 1.2 }}>New Registration</h1>
                            </div>
                        </div>
                        <h2 style={{ fontSize: 34, fontWeight: 800, color: "#fff", fontFamily: "'Playfair Display',serif", lineHeight: 1.2, marginBottom: 16 }}>
                            Find Your <span style={{ color: "#F5D264", fontStyle: "italic" }}>Perfect Match</span>
                        </h2>
                        <p style={{ fontSize: 14.5, color: "rgba(255,255,255,0.65)", lineHeight: 1.75, marginBottom: 32 }}>
                            Create your free account in seconds. View suggested matches and connect with your life partner.
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                            {["இலவசமாக பதிவு · Free to register", "பொருத்தமான சுயவிவரங்கள் · Instant matches", "தனிப்பட்ட பாதுகாப்பு · Private & secure"].map(text => (
                                <div key={text} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    <div style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: "rgba(200,146,10,0.15)", border: "1px solid rgba(200,146,10,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#F5D264" }}>✓</div>
                                    <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontFamily: "'Noto Sans Tamil',sans-serif" }}>{text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <button type="button" onClick={() => history.push("/member/login")}
                        style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 22px", borderRadius: 50, border: "1.5px solid rgba(200,146,10,0.35)", background: "rgba(200,146,10,0.08)", color: "#F5D264", fontSize: 13, fontWeight: 700, cursor: "pointer", width: "fit-content" }}>
                        ← Back to Login
                    </button>
                </div>

                {/* ── Right panel — form ── */}
                <div style={{ background: "#FFFDF5", display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 40px" }}>
                    <div className="w-full max-w-[520px]">
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: "#C8920A", marginBottom: 10 }}>Create Account · இலவச பதிவு</div>
                        <h1 style={{ fontSize: 36, fontWeight: 800, color: "#0A1628", fontFamily: "'Playfair Display',serif", marginBottom: 6, lineHeight: 1.2 }}>Register Free</h1>
                        <p style={{ fontSize: 14, color: "#5C4A2A", lineHeight: 1.7, marginBottom: 28 }}>
                            சில விவரங்களை நிரப்புங்கள். பின்னர் சுயவிவரத்தை முழுமையாக்கலாம்.
                        </p>

                        <div className="mt-8 rounded-[30px] border border-borderColor bg-white p-5 shadow-soft md:p-7">
                            <div className="grid grid-cols-1 gap-4">

                                {/* Name row */}
                                <div className="grid grid-cols-2 gap-3">
                                    <FieldInput
                                        label="First Name"
                                        outerInjectClass="w-full"
                                        fieldProps={form.getFieldProps("firstName")}
                                        fieldHelper={form.getFieldHelpers("firstName")}
                                        fieldMeta={form.getFieldMeta("firstName")}
                                        edit={true}
                                        dataLoaded={true}
                                    />
                                    <FieldInput
                                        label="Last Name"
                                        outerInjectClass="w-full"
                                        fieldProps={form.getFieldProps("lastName")}
                                        fieldHelper={form.getFieldHelpers("lastName")}
                                        fieldMeta={form.getFieldMeta("lastName")}
                                        edit={true}
                                        dataLoaded={true}
                                    />
                                </div>

                                {/* Mobile */}
                                <FieldInput
                                    type="phone"
                                    label="Mobile Number"
                                    outerInjectClass="w-full"
                                    fieldProps={form.getFieldProps("mobileNumber")}
                                    fieldHelper={form.getFieldHelpers("mobileNumber")}
                                    fieldMeta={form.getFieldMeta("mobileNumber")}
                                    edit={true}
                                    dataLoaded={true}
                                />

                                {/* Gender */}
                                <div className="flex flex-col gap-1.5">
                                    <p className="text-sm font-semibold text-tColor">Gender</p>
                                    <div className="flex gap-3">
                                        {GENDER_OPTIONS.map((g) => (
                                            <label key={g.value}
                                                className={`flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 py-3 text-sm font-bold transition
                                                    ${form.values.gender === g.value
                                                        ? "border-primeColor bg-primeSoft text-primeColor"
                                                        : "border-borderColor bg-white text-tColor hover:border-primeColor/50"}`}>
                                                <input
                                                    type="radio"
                                                    name="gender"
                                                    value={g.value}
                                                    checked={form.values.gender === g.value}
                                                    onChange={() => form.setFieldValue("gender", g.value)}
                                                    className="hidden"
                                                />
                                                {g.value === "Male" ? "👨" : "👩"} {g.label}
                                            </label>
                                        ))}
                                    </div>
                                    {form.touched.gender && form.errors.gender && (
                                        <p className="text-xs text-red-500">{form.errors.gender}</p>
                                    )}
                                </div>

                                {/* Password */}
                                <FieldInput
                                    type="password"
                                    label="Password"
                                    outerInjectClass="w-full"
                                    fieldProps={form.getFieldProps("password")}
                                    fieldHelper={form.getFieldHelpers("password")}
                                    fieldMeta={form.getFieldMeta("password")}
                                    edit={true}
                                    dataLoaded={true}
                                />
                                <p className="-mt-2 text-xs text-tpColor">Minimum 8 characters</p>

                                {/* Confirm Password */}
                                <FieldInput
                                    type="password"
                                    label="Confirm Password"
                                    outerInjectClass="w-full"
                                    fieldProps={form.getFieldProps("confirmPassword")}
                                    fieldHelper={form.getFieldHelpers("confirmPassword")}
                                    fieldMeta={form.getFieldMeta("confirmPassword")}
                                    edit={true}
                                    dataLoaded={true}
                                />

                                <Button
                                    type="primary"
                                    injectClass="mt-2 w-full"
                                    onClick={() => form.submitForm()}
                                    disabled={submitting}
                                >
                                    <FiUserPlus /> {submitting ? "Creating Account…" : "Create Account"}
                                </Button>

                                <p className="text-center text-sm text-tpColor">
                                    Already have an account?{" "}
                                    <button
                                        type="button"
                                        onClick={() => history.push("/member/login")}
                                        className="font-bold text-primeColor hover:underline"
                                    >
                                        Sign in
                                    </button>
                                </p>

                            </div>
                        </div>

                        <p className="mt-6 text-center text-xs text-tpColor/70 leading-5">
                            By registering, your profile will be reviewed by our team. <br />
                            For assistance, contact the admin.
                        </p>
                    </div>
                </div>

            </div>
        </motion.div>
    )
}

export default MemberRegister
